/**
 * Settings: account, application preferences, notifications, members & invitations,
 * roles & permissions matrix, expense categories, compliance types, storage, audit log.
 */
import { EXPENSE_TREATMENTS, HMRC_EXPENSE_CATEGORIES } from "@shared/constants.ts";
import { RANGE_PRESETS } from "@shared/periods.ts";
import { PERMISSION_GROUPS, type Permission } from "@shared/permissions.ts";
import {
  accountSchema,
  changeEmailSchema,
  changePasswordSchema,
  inviteSchema,
  notificationPrefsSchema,
  roleSchema,
  teamSettingsSchema,
} from "@shared/schemas/auth.ts";
import { expenseCategorySchema } from "@shared/schemas/finance.ts";
import { complianceTypeSchema } from "@shared/schemas/operations.ts";
import type { MeResponse } from "@shared/types.ts";
import { type ReactNode, useState } from "react";
import { Alert, Badge, Button, Col, Form, ListGroup, Nav, Row, Table } from "react-bootstrap";
import { FormProvider } from "react-hook-form";
import { NavLink, Outlet } from "react-router";
import { type AuditEntry, ChangeList } from "../../components/AuditHistory.tsx";
import {
  CheckboxField,
  FormError,
  NumberField,
  SelectField,
  TextField,
} from "../../components/forms/fields.tsx";
import { ModalFormButton } from "../../components/forms/ModalForm.tsx";
import { Icon } from "../../components/ui/Icon.tsx";
import {
  ConfirmButton,
  DefinitionList,
  PageHeader,
  QueryState,
  Section,
  StatusBadge,
  SubmitButton,
} from "../../components/ui/kit.tsx";
import { api } from "../../lib/api.ts";
import { useAuth } from "../../lib/auth.tsx";
import { formatBytes, useFormat } from "../../lib/format.ts";
import { useSchemaForm } from "../../lib/forms.ts";
import { useApi, useInvalidateAll } from "../../lib/query.ts";
import type { BillingSummary } from "./BillingPage.tsx";
import { DeleteAccountSection } from "./DataPages.tsx";
import { SecuritySection } from "./SecuritySection.tsx";

const SECTIONS = [
  { to: "/settings/account", label: "Account" },
  { to: "/settings/billing", label: "Plan & billing" },
  { to: "/settings/notifications", label: "Notifications" },
  { to: "/settings/application", label: "Application", permission: "team:manage" },
  { to: "/settings/members", label: "Members & invitations" },
  { to: "/settings/roles", label: "Roles & permissions" },
  { to: "/settings/categories", label: "Expense categories" },
  { to: "/settings/compliance-types", label: "Compliance types" },
  { to: "/settings/storage", label: "Storage & email", permission: "team:manage" },
  { to: "/settings/audit", label: "Audit log", permission: "audit:read" },
  { to: "/settings/export", label: "Export & close team", permission: "team:manage" },
] as const;

export function SettingsLayout() {
  const { can } = useAuth();
  return (
    <>
      <PageHeader title="Settings" />
      <Row>
        <Col lg={3} className="mb-3">
          <Nav className="flex-column nav-pills">
            {SECTIONS.filter((s) => !("permission" in s) || can(s.permission)).map((s) => (
              <Nav.Link key={s.to} as={NavLink} to={s.to}>
                {s.label}
              </Nav.Link>
            ))}
          </Nav>
        </Col>
        <Col lg={9}>
          <Outlet />
        </Col>
      </Row>
    </>
  );
}

function InlineForm<T>({
  schema,
  defaults,
  onSubmit,
  submitLabel,
  success,
  children,
}: {
  schema: Parameters<typeof useSchemaForm<T>>[0];
  defaults: Record<string, unknown>;
  onSubmit: (v: T) => Promise<unknown>;
  submitLabel: string;
  success?: string;
  children: ReactNode;
}) {
  const { form, formError, submit } = useSchemaForm(schema, defaults);
  const [done, setDone] = useState(false);
  return (
    <FormProvider {...form}>
      <form
        noValidate
        onSubmit={submit(async (v) => {
          setDone(false);
          await onSubmit(v);
          setDone(true);
        })}
      >
        <FormError message={formError} />
        {done && success && (
          <Alert variant="success" className="py-2">
            {success}
          </Alert>
        )}
        {children}
        <SubmitButton busy={form.formState.isSubmitting}>{submitLabel}</SubmitButton>
      </form>
    </FormProvider>
  );
}

interface SessionInfo {
  id: string;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  lastSeenAt: string;
  current: boolean;
}

export function AccountSettingsPage() {
  const { me, setMe } = useAuth();
  const f = useFormat();
  const invalidate = useInvalidateAll();
  const sessions = useApi<SessionInfo[]>("/api/auth/sessions");
  if (!me) return null;
  return (
    <>
      <Section title="Profile">
        <InlineForm
          schema={accountSchema}
          defaults={{ name: me.user.name }}
          submitLabel="Save"
          success="Profile updated."
          onSubmit={async (v) => setMe(await api.put<MeResponse>("/api/account", v))}
        >
          <TextField name="name" label="Name" required />
        </InlineForm>
      </Section>
      <Section title="Email address">
        <p className="small text-muted">
          Current: {me.user.email}. We'll send a confirmation link to the new address.
        </p>
        <InlineForm
          schema={changeEmailSchema}
          defaults={{ newEmail: "", password: "" }}
          submitLabel="Change email"
          success="Check your inbox to confirm the new address."
          onSubmit={(v) => api.post("/api/auth/change-email", v)}
        >
          <Row>
            <Col md={6}>
              <TextField name="newEmail" label="New email" type="email" required />
            </Col>
            <Col md={6}>
              <TextField
                name="password"
                label="Current password"
                type="password"
                autoComplete="current-password"
                required
              />
            </Col>
          </Row>
        </InlineForm>
      </Section>
      <Section title="Password">
        <InlineForm
          schema={changePasswordSchema}
          defaults={{ currentPassword: "", newPassword: "" }}
          submitLabel="Change password"
          success="Password changed. Other sessions have been signed out."
          onSubmit={(v) => api.post("/api/auth/change-password", v)}
        >
          <Row>
            <Col md={6}>
              <TextField
                name="currentPassword"
                label="Current password"
                type="password"
                autoComplete="current-password"
                required
              />
            </Col>
            <Col md={6}>
              <TextField
                name="newPassword"
                label="New password"
                type="password"
                autoComplete="new-password"
                hint="At least 10 characters."
                required
              />
            </Col>
          </Row>
        </InlineForm>
      </Section>
      <Section
        title="Active sessions"
        actions={
          <Button
            size="sm"
            variant="outline-danger"
            onClick={async () => {
              await api.post("/api/auth/sessions/revoke-others");
              await invalidate();
            }}
          >
            Sign out other sessions
          </Button>
        }
        flush
      >
        <QueryState query={sessions}>
          {(list) => (
            <ListGroup variant="flush">
              {list.map((s) => (
                <ListGroup.Item
                  key={s.id}
                  className="d-flex justify-content-between align-items-center gap-2"
                >
                  <div className="min-w-0">
                    <div className="text-truncate small">{s.userAgent ?? "Unknown device"}</div>
                    <div className="small text-muted">
                      {s.ip ?? "—"} · last active {f.dateTime(s.lastSeenAt)}
                    </div>
                  </div>
                  {s.current ? (
                    <Badge bg="success">This device</Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={async () => {
                        await api.delete(`/api/auth/sessions/${s.id}`);
                        await invalidate();
                      }}
                    >
                      Sign out
                    </Button>
                  )}
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </QueryState>
      </Section>
      <SecuritySection />
      <DeleteAccountSection />
    </>
  );
}

export function NotificationSettingsPage() {
  const q = useApi<Record<string, unknown>>("/api/account/notifications");
  const types = [
    ["task", "Tasks due and overdue"],
    ["compliance", "Compliance expiring"],
    ["rent", "Rent arrears"],
    ["tenancy", "Tenancies ending"],
    ["finance", "Mortgage deals ending"],
    ["maintenance", "Urgent maintenance"],
    ["mtd", "Making Tax Digital deadlines"],
  ];
  return (
    <Section title="Notifications & reminders">
      <QueryState query={q}>
        {(prefs) => (
          <InlineForm
            schema={notificationPrefsSchema}
            defaults={prefs}
            submitLabel="Save preferences"
            success="Preferences saved."
            onSubmit={(v) => api.put("/api/account/notifications", v)}
          >
            <CheckboxField name="inAppEnabled" label="Show reminders in the app" />
            <CheckboxField name="emailEnabled" label="Send reminders by email" />
            <SelectField
              name="digest"
              label="Email delivery"
              options={[
                { value: "off", label: "Immediately, one email per reminder" },
                { value: "daily", label: "Daily digest" },
                { value: "weekly", label: "Weekly digest" },
              ]}
            />
            <h3 className="h6 mt-3">Remind me about</h3>
            {types.map(([key, label]) => (
              <CheckboxField key={key} name={`types.${key}`} label={label} />
            ))}
            <NumberField
              name="taskReminderDays"
              label="Default task reminder (days before due)"
              min={0}
              max={90}
            />
          </InlineForm>
        )}
      </QueryState>
    </Section>
  );
}

export function ApplicationSettingsPage() {
  const { me: current } = useAuth();
  const isOwner = current?.teams.find((t) => t.id === current.activeTeamId)?.isOwner ?? false;
  const q = useApi<{ name: string; settings: Record<string, unknown> }>("/api/team");
  const { refresh } = useAuth();
  return (
    <Section title="Application preferences">
      <QueryState query={q}>
        {(team) => (
          <InlineForm
            schema={teamSettingsSchema}
            defaults={{ name: team.name, ...team.settings }}
            submitLabel="Save"
            success="Settings saved."
            onSubmit={async (v) => {
              await api.put("/api/team", v);
              await refresh();
            }}
          >
            <TextField name="name" label="Team / portfolio name" required />
            <Row>
              <Col md={6}>
                <SelectField
                  name="dateFormat"
                  label="Date format"
                  options={[
                    { value: "d MMM yyyy", label: "6 Apr 2025" },
                    { value: "dd/MM/yyyy", label: "06/04/2025" },
                  ]}
                />
              </Col>
              <Col md={6}>
                <TextField name="timezone" label="Timezone" hint="IANA name, e.g. Europe/London" />
              </Col>
              <Col md={6}>
                <SelectField
                  name="defaultRange"
                  label="Default reporting period"
                  options={RANGE_PRESETS.filter((p) => p.value !== "custom")}
                />
              </Col>
              <Col md={6}>
                <NumberField
                  name="complianceWarningDays"
                  label="Warn about compliance expiring within (days)"
                  min={1}
                  max={365}
                />
              </Col>
            </Row>
            {isOwner && (
              <CheckboxField
                name="requireMfa"
                label="Require two-step verification for every member"
                hint="Members without it are asked to set it up before they can see the team's records. Only Owners can change this."
              />
            )}
            <p className="small text-muted">Currency: GBP (£). Amounts are stored to the penny.</p>
          </InlineForm>
        )}
      </QueryState>
    </Section>
  );
}

interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  isSystem: boolean;
  memberCount: number;
}

export function MembersPage() {
  const f = useFormat();
  const { me, can } = useAuth();
  const invalidate = useInvalidateAll();
  const members =
    useApi<
      Array<{
        id: string;
        userId: string;
        name: string;
        /** Only returned to members with members:manage. */
        email?: string;
        roleId: string;
        roleName: string;
        lastLoginAt?: string | null;
        /** Only returned to members with members:manage (#49). */
        emailSuppressed?: "bounce" | "complaint" | "manual" | null;
      }>
    >("/api/team/members");
  const roles = useApi<Role[]>("/api/team/roles");
  const invites = useApi<
    Array<{ id: string; email: string; roleName: string; expiresAt: string; invitedByName: string | null }>
  >(can("members:manage") ? "/api/team/invitations" : null);
  // Only roles made entirely of permissions you hold can be granted (the server enforces this too).
  const grantable = (roleId: string) =>
    (roles.data ?? []).some((r) => r.id === roleId && r.permissions.every((p) => can(p as Permission)));
  const billing = useApi<BillingSummary>("/api/billing");
  const roleOptions = (roles.data ?? [])
    .filter((r) => grantable(r.id))
    .map((r) => ({ value: r.id, label: r.name }));
  return (
    <>
      <Section
        title="Members"
        flush
        actions={
          can("members:manage") && (
            <ModalFormButton
              label={
                <>
                  <Icon name="plus" size={14} /> Invite member
                </>
              }
              title="Invite a team member"
              schema={inviteSchema}
              defaults={{ email: "", roleId: roleOptions.find((r) => r.label === "Viewer")?.value ?? "" }}
              onSubmit={(v) => api.post("/api/team/invitations", v)}
              submitLabel="Send invitation"
            >
              {() => (
                <>
                  <TextField name="email" label="Email address" type="email" required />
                  <SelectField
                    name="roleId"
                    label="Role"
                    options={roleOptions}
                    required
                    hint="You can change their role later."
                  />
                  {billing.data?.billingEnabled && !billing.data.exempt && (
                    <Alert variant="info" className="small mb-0">
                      Each member is a seat. When they accept, your bill goes up by £
                      {(billing.data.prices.monthPence / 100).toFixed(2)} a month (or £
                      {(billing.data.prices.yearPence / 100).toFixed(2)} a year), pro rata.
                    </Alert>
                  )}
                </>
              )}
            </ModalFormButton>
          )
        }
      >
        <QueryState query={members}>
          {(list) => (
            <Table responsive className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  {can("members:manage") && <th>Last sign-in</th>}
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((m) => (
                  <tr key={m.id}>
                    <td>
                      {m.name}
                      {m.userId === me?.user.id && (
                        <Badge bg="light" text="dark" className="ms-1">
                          You
                        </Badge>
                      )}
                      {m.email && <div className="small text-muted">{m.email}</div>}
                      {m.emailSuppressed && (
                        <Badge
                          bg="warning"
                          text="dark"
                          title="Emails to this address bounced or were reported as spam, so none are sent"
                        >
                          Emails not delivered
                        </Badge>
                      )}
                    </td>
                    <td>
                      {can("members:manage") && m.userId !== me?.user.id && grantable(m.roleId) ? (
                        <Form.Select
                          size="sm"
                          aria-label={`Role for ${m.name}`}
                          value={m.roleId}
                          onChange={async (e) => {
                            try {
                              await api.put(`/api/team/members/${m.id}`, { roleId: e.target.value });
                            } catch (err) {
                              alert((err as Error).message);
                            }
                            await invalidate();
                          }}
                          style={{ width: "auto" }}
                        >
                          {roleOptions.map((r) => (
                            <option key={r.value} value={r.value}>
                              {r.label}
                            </option>
                          ))}
                        </Form.Select>
                      ) : (
                        m.roleName
                      )}
                    </td>
                    {can("members:manage") && <td className="small">{f.dateTime(m.lastLoginAt ?? null)}</td>}
                    <td className="text-end">
                      {can("members:manage") && m.userId !== me?.user.id && grantable(m.roleId) && (
                        <ConfirmButton
                          title={`Remove ${m.name}?`}
                          body="They will immediately lose access to this team's data."
                          confirmLabel="Remove"
                          onConfirm={async () => {
                            await api.delete(`/api/team/members/${m.id}`);
                            await invalidate();
                          }}
                        >
                          Remove
                        </ConfirmButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </QueryState>
      </Section>
      {can("members:manage") && (
        <Section title="Pending invitations" flush>
          <QueryState query={invites}>
            {(list) =>
              list.length === 0 ? (
                <p className="small text-muted p-3 mb-0">No pending invitations.</p>
              ) : (
                <Table className="mb-0">
                  <tbody>
                    {list.map((i) => (
                      <tr key={i.id}>
                        <td>
                          {i.email}
                          <div className="small text-muted">
                            {i.roleName} · expires {f.date(i.expiresAt)}
                          </div>
                        </td>
                        <td className="text-end">
                          <ConfirmButton
                            title="Revoke invitation?"
                            body="The invitation link will stop working."
                            confirmLabel="Revoke"
                            onConfirm={async () => {
                              await api.delete(`/api/team/invitations/${i.id}`);
                              await invalidate();
                            }}
                          >
                            Revoke
                          </ConfirmButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )
            }
          </QueryState>
        </Section>
      )}
    </>
  );
}

function PermissionMatrix({ form }: { form: import("../../components/forms/fields.tsx").AnyForm }) {
  const selected = new Set((form.watch("permissions") as string[]) ?? []);
  const toggle = (p: string, on: boolean) => {
    const next = new Set(selected);
    if (on) next.add(p);
    else next.delete(p);
    form.setValue("permissions", [...next].sort(), { shouldDirty: true });
  };
  const actions = ["read", "write", "delete", "export", "submit", "manage"];
  return (
    <div className="table-responsive">
      <Table size="sm" className="align-middle">
        <thead>
          <tr>
            <th>Area</th>
            {actions.map((a) => (
              <th key={a} className="text-center text-capitalize">
                {a}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {PERMISSION_GROUPS.map((g) => (
            <tr key={g.resource}>
              <td>{g.label}</td>
              {actions.map((a) => {
                const perm = `${g.resource}:${a}`;
                return (
                  <td key={a} className="text-center">
                    {(g.actions as readonly string[]).includes(a) ? (
                      <Form.Check
                        aria-label={`${g.label} ${a}`}
                        checked={selected.has(perm)}
                        onChange={(e) => toggle(perm, e.target.checked)}
                      />
                    ) : null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

export function RolesPage() {
  const q = useApi<Role[]>("/api/team/roles");
  const { can } = useAuth();
  const invalidate = useInvalidateAll();
  const editor = (role: Role | null) => (
    <ModalFormButton
      label={
        role ? (
          "Edit"
        ) : (
          <>
            <Icon name="plus" size={14} /> New role
          </>
        )
      }
      variant={role ? "outline-secondary" : "outline-primary"}
      size="lg"
      title={role ? `Edit role: ${role.name}` : "New role"}
      schema={roleSchema}
      defaults={{
        name: role?.name ?? "",
        description: role?.description ?? "",
        permissions: role?.permissions ?? [],
      }}
      onSubmit={(v) => (role ? api.put(`/api/team/roles/${role.id}`, v) : api.post("/api/team/roles", v))}
    >
      {(form) => (
        <>
          <Row>
            <Col md={5}>
              <TextField name="name" label="Role name" required />
            </Col>
            <Col md={7}>
              <TextField name="description" label="Description" />
            </Col>
          </Row>
          {role?.isSystem ? (
            <Alert variant="info" className="small">
              The Owner role always has every permission.
            </Alert>
          ) : (
            <PermissionMatrix form={form} />
          )}
        </>
      )}
    </ModalFormButton>
  );
  return (
    <Section title="Roles & permissions" actions={can("roles:manage") && editor(null)} flush>
      <QueryState query={q}>
        {(roles) => (
          <ListGroup variant="flush">
            {roles.map((r) => (
              <ListGroup.Item key={r.id} className="d-flex justify-content-between align-items-start gap-2">
                <div>
                  <div className="fw-semibold">
                    {r.name} {r.isSystem && <Badge bg="dark">System</Badge>}
                  </div>
                  <div className="small text-muted">{r.description}</div>
                  <div className="small text-muted">
                    {r.permissions.length} permissions · {r.memberCount} member
                    {r.memberCount === 1 ? "" : "s"}
                  </div>
                </div>
                {can("roles:manage") && (
                  <div className="d-flex gap-2">
                    {editor(r)}
                    {!r.isSystem && (
                      <ConfirmButton
                        title={`Delete role ${r.name}?`}
                        body="Roles with members can't be deleted."
                        onConfirm={async () => {
                          await api.delete(`/api/team/roles/${r.id}`);
                          await invalidate();
                        }}
                      >
                        <Icon name="trash" size={14} />
                      </ConfirmButton>
                    )}
                  </div>
                )}
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </QueryState>
    </Section>
  );
}

interface Category {
  id: string;
  name: string;
  treatment: string;
  hmrcCategory: string | null;
  sortOrder: number;
  isArchived: boolean;
  expenseCount: number;
}

export function CategoriesPage() {
  const f = useFormat();
  const { can } = useAuth();
  const invalidate = useInvalidateAll();
  const q = useApi<Category[]>("/api/expense-categories");
  const editor = (c: Category | null) => (
    <ModalFormButton
      label={
        c ? (
          "Edit"
        ) : (
          <>
            <Icon name="plus" size={14} /> New category
          </>
        )
      }
      variant={c ? "outline-secondary" : "outline-primary"}
      title={c ? "Edit category" : "New expense category"}
      schema={expenseCategorySchema}
      defaults={{
        name: c?.name ?? "",
        treatment: c?.treatment ?? "revenue",
        hmrcCategory: c?.hmrcCategory ?? "",
        sortOrder: c?.sortOrder ?? 0,
        isArchived: c?.isArchived ?? false,
      }}
      onSubmit={(v) =>
        c ? api.put(`/api/expense-categories/${c.id}`, v) : api.post("/api/expense-categories", v)
      }
    >
      {() => (
        <>
          <TextField name="name" label="Name" required />
          <SelectField name="treatment" label="Treatment" options={EXPENSE_TREATMENTS} />
          <SelectField
            name="hmrcCategory"
            label="HMRC category (revenue expenses)"
            options={HMRC_EXPENSE_CATEGORIES}
            placeholder="Not mapped (defaults to Other)"
          />
          <NumberField name="sortOrder" label="Sort order" min={0} />
          <CheckboxField name="isArchived" label="Archived (hidden from new expenses)" />
          {c && c.expenseCount > 0 && (
            <Alert variant="warning" className="small mb-0">
              Changing treatment or HMRC mapping affects {c.expenseCount} existing expenses in reports. The
              change is audited.
            </Alert>
          )}
        </>
      )}
    </ModalFormButton>
  );
  return (
    <Section title="Expense categories" actions={can("team:manage") && editor(null)} flush>
      <Alert variant="light" className="small m-3 disclaimer">
        Categories are labels to organise your records and map them to HMRC reporting categories. They do not
        determine whether a cost is tax deductible.
      </Alert>
      <QueryState query={q}>
        {(list) => (
          <Table responsive className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Name</th>
                <th>Treatment</th>
                <th>HMRC category</th>
                <th className="text-end">Expenses</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} className={c.isArchived ? "text-muted" : undefined}>
                  <td>
                    {c.name} {c.isArchived && <StatusBadge status="archived" />}
                  </td>
                  <td className="small">{f.label(EXPENSE_TREATMENTS, c.treatment)}</td>
                  <td className="small">
                    {c.hmrcCategory ? f.label(HMRC_EXPENSE_CATEGORIES, c.hmrcCategory) : "—"}
                  </td>
                  <td className="text-end">{c.expenseCount}</td>
                  <td className="text-end text-nowrap">
                    {can("team:manage") && (
                      <>
                        {editor(c)}{" "}
                        {c.expenseCount === 0 && (
                          <ConfirmButton
                            title="Delete category?"
                            body="This category has no expenses."
                            onConfirm={async () => {
                              await api.delete(`/api/expense-categories/${c.id}`);
                              await invalidate();
                            }}
                          >
                            <Icon name="trash" size={14} />
                          </ConfirmButton>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </QueryState>
    </Section>
  );
}

interface ComplianceType {
  id: string;
  name: string;
  description: string;
  validityMonths: number | null;
  reminderDays: number[];
  isArchived: boolean;
  itemCount: number;
}

export function ComplianceTypesPage() {
  const { can } = useAuth();
  const q = useApi<ComplianceType[]>("/api/compliance-types");
  const editor = (t: ComplianceType | null) => (
    <ModalFormButton
      label={
        t ? (
          "Edit"
        ) : (
          <>
            <Icon name="plus" size={14} /> New requirement
          </>
        )
      }
      variant={t ? "outline-secondary" : "outline-primary"}
      title={t ? "Edit requirement type" : "New requirement type"}
      schema={complianceTypeSchema}
      defaults={{
        name: t?.name ?? "",
        description: t?.description ?? "",
        validityMonths: t?.validityMonths ?? "",
        reminderDays: t?.reminderDays ?? [30],
        isArchived: t?.isArchived ?? false,
      }}
      onSubmit={(v) =>
        t ? api.put(`/api/compliance-types/${t.id}`, v) : api.post("/api/compliance-types", v)
      }
    >
      {(form) => (
        <>
          <TextField name="name" label="Name" required />
          <TextField name="description" label="Description" />
          <NumberField name="validityMonths" label="Valid for (months)" min={1} />
          <Form.Group className="mb-3" controlId="ct-reminders">
            <Form.Label>Remind before expiry (days)</Form.Label>
            <Form.Control
              defaultValue={((form.getValues("reminderDays") as number[]) ?? []).join(", ")}
              onChange={(e) =>
                form.setValue(
                  "reminderDays",
                  e.target.value
                    .split(/[,\s]+/)
                    .filter(Boolean)
                    .map(Number)
                    .filter((n) => Number.isInteger(n) && n >= 0),
                )
              }
            />
          </Form.Group>
          <CheckboxField name="isArchived" label="Archived" />
        </>
      )}
    </ModalFormButton>
  );
  return (
    <Section title="Compliance requirement types" actions={can("compliance:write") && editor(null)} flush>
      <QueryState query={q}>
        {(list) => (
          <Table responsive className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Requirement</th>
                <th>Validity</th>
                <th>Reminders</th>
                <th className="text-end">Records</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((t) => (
                <tr key={t.id} className={t.isArchived ? "text-muted" : undefined}>
                  <td>{t.name}</td>
                  <td>{t.validityMonths ? `${t.validityMonths} months` : "—"}</td>
                  <td className="small">{t.reminderDays.join(", ")} days before</td>
                  <td className="text-end">{t.itemCount}</td>
                  <td className="text-end">{can("compliance:write") && editor(t)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </QueryState>
    </Section>
  );
}

export function StorageSettingsPage() {
  const f = useFormat();
  const status = useApi<{
    connected: boolean;
    problem: string | null;
    endpoint: string;
    bucket: string;
    region: string;
    maxUploadBytes: number;
    documents: number;
    bytes: number;
  }>("/api/storage/status");
  const emails =
    useApi<
      Array<{
        id: string;
        toEmail: string;
        subject: string;
        status: string;
        error: string | null;
        createdAt: string;
      }>
    >("/api/team/email-log");
  return (
    <>
      <Section title="Document storage">
        <QueryState query={status}>
          {(s) => (
            <>
              {s.problem && (
                <Alert variant="danger" className="small">
                  {s.problem}
                </Alert>
              )}
              <DefinitionList
                items={[
                  [
                    "Status",
                    s.connected ? (
                      <StatusBadge key="s" status="current" label="Connected" />
                    ) : (
                      <StatusBadge key="s" status="expired" label="Unreachable" />
                    ),
                  ],
                  ["Endpoint", s.endpoint],
                  ["Bucket", s.bucket],
                  ["Region", s.region],
                  ["Documents", `${s.documents} (${formatBytes(s.bytes)})`],
                  ["Maximum upload size", formatBytes(s.maxUploadBytes)],
                ]}
              />
            </>
          )}
        </QueryState>
        <p className="small text-muted mb-0">
          Storage is configured with environment variables (S3_*). Credentials are never sent to the browser.
        </p>
      </Section>
      <Section title="Recent emails" flush>
        <QueryState query={emails}>
          {(list) =>
            list.length === 0 ? (
              <p className="small text-muted p-3 mb-0">No emails sent yet.</p>
            ) : (
              <Table responsive size="sm" className="mb-0">
                <tbody>
                  {list.map((e) => (
                    <tr key={e.id}>
                      <td className="small">{f.dateTime(e.createdAt)}</td>
                      <td className="small">{e.toEmail}</td>
                      <td className="small">{e.subject}</td>
                      <td>
                        <StatusBadge
                          status={
                            e.status === "sent"
                              ? "completed"
                              : e.status === "failed" || e.status === "suppressed"
                                ? "expired"
                                : "open"
                          }
                          label={e.status === "suppressed" ? "not sent (undeliverable)" : e.status}
                        />
                        {e.error && <div className="small text-danger">{e.error}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )
          }
        </QueryState>
      </Section>
    </>
  );
}

export function AuditLogPage() {
  const f = useFormat();
  const [page, setPage] = useState(1);
  const q = useApi<AuditEntry[]>("/api/audit", { page }, { keepPrevious: true });
  return (
    <Section title="Audit log" flush>
      <QueryState query={q}>
        {(list) => (
          <>
            <Table responsive className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Record</th>
                  <th>Change</th>
                </tr>
              </thead>
              <tbody>
                {list.map((e) => (
                  <tr key={e.id}>
                    <td className="small text-nowrap">{f.dateTime(e.createdAt)}</td>
                    <td className="small">{e.userName ?? "System"}</td>
                    <td className="small text-capitalize">
                      {e.entityType.replaceAll("_", " ")} · {e.action}
                    </td>
                    <td>
                      <ChangeList entry={e} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="d-flex justify-content-between p-3 border-top">
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Newer
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={list.length < 50}
                onClick={() => setPage(page + 1)}
              >
                Older
              </Button>
            </div>
          </>
        )}
      </QueryState>
    </Section>
  );
}
